import api from './apiClient';
import { getCloudFrontUrl, getS3Url } from './config';
import {
  chunkedUploadVideo,
  type ChunkedUploadProgress,
  type ChunkedUploadController,
} from './chunkedUpload';

export type { ChunkedUploadProgress, ChunkedUploadController };

export interface UploadPresignedUrl {
  uploadUrl: string;
  key: string;
}

export interface UploadResult {
  key: string;
  url: string;
  size: number;
}

export type UploadFolder = 'videos' | 'thumbnails' | 'profiles' | 'covers' | 'series-trailers' | 'subtitles';

const CHUNKED_THRESHOLD = 10 * 1024 * 1024; // 10 MB

function detectImageContentType(uri: string, blob?: Blob): string {
  if (blob?.type && blob.type.startsWith('image/')) return blob.type;
  if (uri.startsWith('data:image/png')) return 'image/png';
  if (uri.startsWith('data:image/webp')) return 'image/webp';
  if (uri.startsWith('data:image/gif')) return 'image/gif';
  const ext = uri.split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  return 'image/jpeg';
}

export async function getUploadUrl(
  contentType: string,
  folder: UploadFolder,
  options?: { seriesId?: string; episodeId?: string }
): Promise<UploadPresignedUrl | null> {
  const result = await api.post<UploadPresignedUrl>('/storage/upload-url', {
    contentType,
    folder,
    ...(options?.seriesId && { seriesId: options.seriesId }),
    ...(options?.episodeId && { episodeId: options.episodeId }),
  });

  if (result.error || !result.data) {
    console.log('Get upload URL error:', result.error);
    return null;
  }

  return result.data;
}

export async function uploadFile(
  uri: string,
  contentType: string,
  folder: UploadFolder,
  options?: { seriesId?: string; episodeId?: string; sourceBlob?: Blob }
): Promise<UploadResult | null> {
  const presigned = await getUploadUrl(contentType, folder, options);
  if (!presigned) {
    console.error(`[upload] Failed to get presigned URL for ${folder}`);
    return null;
  }

  try {
    let blob: Blob;
    if (options?.sourceBlob) {
      blob = options.sourceBlob;
    } else {
      const fileResponse = await fetch(uri);
      if (!fileResponse.ok) {
        console.error(`[upload] Failed to fetch blob from URI: HTTP ${fileResponse.status}`);
        return null;
      }
      blob = await fileResponse.blob();
    }

    if (blob.type && blob.type !== contentType) {
      blob = new Blob([blob], { type: contentType });
    }

    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: blob,
    });

    if (!uploadResponse.ok) {
      const detail = await uploadResponse.text().catch(() => '');
      console.error(`[upload] S3 PUT failed for ${folder}: HTTP ${uploadResponse.status}`, detail);
      return null;
    }

    const url = getCloudFrontUrl(presigned.key) || getS3Url(presigned.key);

    return {
      key: presigned.key,
      url,
      size: blob.size,
    };
  } catch (error) {
    console.error(`[upload] Upload error for ${folder}:`, error);
    return null;
  }
}

/**
 * Uploads a video file, automatically choosing chunked multipart upload for
 * files larger than CHUNKED_THRESHOLD and single PUT for smaller files.
 * When using chunked upload, the optional onProgress callback fires with
 * per-part progress and the returned controller allows aborting mid-upload.
 */
export function uploadVideoSmart(
  uri: string,
  contentType: string,
  folder: UploadFolder,
  options?: { seriesId?: string; episodeId?: string; sourceBlob?: Blob },
  onProgress?: (progress: ChunkedUploadProgress) => void,
): ChunkedUploadController {
  let aborted = false;
  let innerAbort: (() => void) | null = null;

  const promise = (async (): Promise<UploadResult> => {
    let blob: Blob;
    if (options?.sourceBlob) {
      blob = options.sourceBlob;
    } else {
      const headResponse = await fetch(uri);
      blob = await headResponse.blob();
    }
    const fileSize = blob.size;

    if (fileSize >= CHUNKED_THRESHOLD) {
      const ctrl = chunkedUploadVideo(uri, contentType, folder, { ...options, sourceBlob: blob }, onProgress);
      innerAbort = ctrl.abort;
      if (aborted) {
        ctrl.abort();
        throw new Error('Upload aborted');
      }
      return ctrl.promise;
    }

    // Small file — single PUT upload
    onProgress?.({
      loaded: 0,
      total: fileSize,
      currentPart: 1,
      totalParts: 1,
      stage: 'Uploading…',
    });

    const presigned = await getUploadUrl(contentType, folder, options);
    if (!presigned) throw new Error('Failed to get upload URL');
    if (aborted) throw new Error('Upload aborted');

    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: HTTP ${uploadResponse.status}`);
    }

    const url = getCloudFrontUrl(presigned.key) || getS3Url(presigned.key);

    onProgress?.({
      loaded: fileSize,
      total: fileSize,
      currentPart: 1,
      totalParts: 1,
      stage: 'Done',
    });

    return { key: presigned.key, url, size: fileSize };
  })();

  return {
    promise,
    abort: () => {
      aborted = true;
      innerAbort?.();
    },
  };
}

// --- Convenience wrappers ---

export async function uploadProfilePicture(uri: string): Promise<string | null> {
  const result = await uploadFile(uri, 'image/jpeg', 'profiles');
  return result?.url ?? null;
}

export async function uploadVideoFile(
  uri: string,
  onProgress?: (progress: ChunkedUploadProgress) => void,
): Promise<UploadResult | null> {
  try {
    const ctrl = uploadVideoSmart(uri, 'video/mp4', 'videos', undefined, onProgress);
    return await ctrl.promise;
  } catch {
    return null;
  }
}

export async function uploadThumbnail(uri: string): Promise<string | null> {
  const result = await uploadFile(uri, 'image/jpeg', 'thumbnails');
  return result?.url ?? null;
}

export async function uploadCoverImage(uri: string): Promise<string | null> {
  const result = await uploadFile(uri, 'image/jpeg', 'covers');
  return result?.url ?? null;
}

export async function uploadSeriesCover(uri: string, sourceBlob?: Blob): Promise<UploadResult | null> {
  const contentType = detectImageContentType(uri, sourceBlob);
  return uploadFile(uri, contentType, 'covers', sourceBlob ? { sourceBlob } : undefined);
}

export function uploadSeriesTrailer(
  uri: string,
  onProgress?: (progress: ChunkedUploadProgress) => void,
  sourceBlob?: Blob,
): ChunkedUploadController {
  return uploadVideoSmart(uri, 'video/mp4', 'series-trailers', sourceBlob ? { sourceBlob } : undefined, onProgress);
}

export function uploadEpisodeVideo(
  uri: string,
  seriesId: string,
  episodeId: string,
  onProgress?: (progress: ChunkedUploadProgress) => void,
  sourceBlob?: Blob,
): ChunkedUploadController {
  return uploadVideoSmart(uri, 'video/mp4', 'videos', { seriesId, episodeId, sourceBlob }, onProgress);
}

export async function uploadEpisodeThumbnail(
  uri: string,
  seriesId: string,
  episodeId: string,
  sourceBlob?: Blob,
): Promise<UploadResult | null> {
  const contentType = detectImageContentType(uri, sourceBlob);
  return uploadFile(uri, contentType, 'thumbnails', { seriesId, episodeId, sourceBlob });
}

export function getVideoStreamUrl(videoKey: string): string {
  if (videoKey.startsWith('http')) return videoKey;
  return getCloudFrontUrl(videoKey);
}

export function getThumbnailUrl(thumbnailKey: string, width?: number): string {
  if (thumbnailKey.startsWith('http')) return thumbnailKey;
  const url = getCloudFrontUrl(thumbnailKey);
  if (width) {
    return `${url}?w=${width}`;
  }
  return url;
}
